/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core'
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http'
import { Observable, throwError } from 'rxjs'
import { catchError, finalize, map } from 'rxjs/operators'
import { LoadingService } from './loading.service'
import { DataService } from '../shared/services/data/data.service'
import { AuthService } from '../services/auth.service'

@Injectable()
export class LoadingInterceptor implements HttpInterceptor {
  loadingService = inject(LoadingService)
  dataService = inject(DataService)
  private authService = inject(AuthService)
  private lastRequestTimeKey = 'lastRequestTime'

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<any>> {
    const skipLoadingSpinner = request.headers.has('skipLoadingSpinner')

    if (!skipLoadingSpinner) {
      this.loadingService.setLoading(true, request.url)
    } else {
      request = request.clone({
        headers: request.headers.delete('skipLoadingSpinner'),
      })
    }

    if (this.isLastRequestTimeGreaterThan(30)) {
      this.authService.logout()
      sessionStorage.clear()

      return throwError('Session expired')
    } else {
      this.setLastRequestTime()
      const companyId = this.dataService.signalLastCompany().companyId ?? 0
      const storeId = request.headers.has('x-store-id')
        ? (request.headers.get('x-store-id') ?? 0)
        : (this.dataService.signalLastStore().storeId ?? 0)
      const username = this.dataService.signalUsername()
      const storeTimezone = Number(this.dataService.signalStoreTimezone() || 0)
      const excludeCompanyInfoHeader = request.headers.has('excludeCompanyInfoHeader')
      const authToken = this.dataService.getItem('authToken')

      const todayInStore = this.getTodayInStoreTimezone(storeTimezone)
      const workDate = this.loadingService.workDate.value
      let workDateWithTimezone = ''
      if (workDate.toDateString() !== todayInStore.toDateString()) {
        let month: string | number = workDate.getMonth() + 1
        month = month >= 10 ? month : `0${month}`
        let day: string | number = workDate.getDate()
        day = day >= 10 ? day : `0${day}`
        const year = workDate.getFullYear()
        workDateWithTimezone = `${year}-${month}-${day}T00:00:00`
      } else {
        workDateWithTimezone = this.createDateInTimeZone(storeTimezone)
      }

      if (!excludeCompanyInfoHeader) {
        request = request.clone({
          setHeaders: {
            'x-company-id': companyId.toString(),
            'x-store-id': storeId.toString(),
            'x-work-date': workDateWithTimezone,
            'x-username': username,
            'x-client-timezone': String(storeTimezone),
            Authorization: `Bearer ${authToken}`,
          },
        })
      } else if (excludeCompanyInfoHeader) {
        request = request.clone({
          headers: request.headers.delete('excludeCompanyInfoHeader'),
        })
      } else {
        request = request.clone({
          setHeaders: {
            Authorization: `Bearer ${authToken}`,
          },
        })
      }

      return this.handleRequest(next, request)
    }
  }

  setLastRequestTime(): void {
    const currentTime = new Date().getTime()
    sessionStorage.setItem(this.lastRequestTimeKey, currentTime.toString())
  }

  isLastRequestTimeGreaterThan(minutes: number): boolean {
    const lastRequestTime = sessionStorage.getItem(this.lastRequestTimeKey)
    if (lastRequestTime) {
      const currentTime = new Date().getTime()
      const differenceInMinutes = (currentTime - parseInt(lastRequestTime, 10)) / 60000
      return differenceInMinutes > minutes
    }
    return false
  }

  handleRequest(next: HttpHandler, request: HttpRequest<unknown>): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        this.loadingService.setLoading(false, request.url)
        return throwError(() => {
          this.errorHandle(error)
          return error
        })
      }),
      map((evt: HttpEvent<unknown>) => {
        if (evt instanceof HttpResponse) {
          this.loadingService.setLoading(false, request.url)
        }
        return evt
      }),
      finalize(() => {
        this.loadingService.setLoading(false, request.url)
      })
    )
  }

  async errorHandle(error: HttpErrorResponse): Promise<void> {
    if (
      (error.status === 401 || error.status === 403) &&
      [
        'authenticate',
        'log-out',
        'forgot-password',
        'change-password',
        'new-mfa',
        'valid-mfa',
        'set-pin',
        'check-pin',
        'token-exchange',
      ].some((url) => error.url?.includes(url)) === false
    ) {
      await this.authService.logout()
    }
  }

  getTodayInStoreTimezone(storeTimezone: number): Date {
    const nowUTC = new Date()
    const offsetMinutes = storeTimezone * 60
    const nowInStoreTimezone = new Date(nowUTC.getTime() + offsetMinutes * 60000)

    const year = nowInStoreTimezone.getUTCFullYear()
    const month = nowInStoreTimezone.getUTCMonth()
    const day = nowInStoreTimezone.getUTCDate()

    return new Date(year, month, day, 0, 0, 0, 0)
  }

  createDateInTimeZone(storeTimezone: number): string {
    const nowUTC = new Date()
    const offsetMinutes = storeTimezone * 60
    const nowInStoreTimezone = new Date(nowUTC.getTime() + offsetMinutes * 60000)

    const year = nowInStoreTimezone.getUTCFullYear()
    const month = String(nowInStoreTimezone.getUTCMonth() + 1).padStart(2, '0')
    const day = String(nowInStoreTimezone.getUTCDate()).padStart(2, '0')
    return `${year}-${month}-${day}T00:00:00`
  }
}
